/* eslint-disable max-lines -- Fixme when deprecated function and `.buildIsHovered$()` is removed. */
import {
  Directive,
  ElementRef,
  HostBinding,
  inject,
  Injector,
  Input,
  OnInit,
} from '@angular/core';
import { Store } from '@sama/app-state';
import { isDefined } from '@sama/js-core';
import { get } from '@sama/micro-dash';
import { DirectiveSuperclass } from '@sama/ng-core';
import { cache, filterBehavior } from '@sama/rxjs-core';
import { combineLatest, merge, Observable, of } from 'rxjs';
import {
  distinctUntilChanged,
  filter,
  map,
  pluck,
  startWith,
  switchMap,
} from 'rxjs/operators';
import {
  isVisible,
  ShapeLocationState2D,
} from '../../../cross-team-shared/annotation/shared/state/shape-location-state';
import { ShapeState2D } from '../../../cross-team-shared/annotation/shared/state/shape-state';
import { MouseEventService } from '../../../cross-team-shared/misc-services/events/mouse-event.service';
import {
  getColorOutput,
  getShapeColorObjectless$,
  getTrainingShapeColor,
} from '../../../cross-team-shared/misc-services/tag-helper';
import { setCssProperty } from '../../../cross-team-shared/misc-utils';
import {
  IN_PROGRESS_INDEX,
  TRAINING_INDEX,
} from '../../../team-apis/core/reserved-shape-indexes';
import {
  Point2D,
  VisibilityState,
} from '../../../team-apis/otterdam/answers/shape-typings';
import { getShapeHelper } from '../shape-helpers/get-shape-helper';
import { AbstractSensorContentStore2D } from '../state/abstract-sensor-content-store-2d';
import { SubpointInfo } from '../subpoint/subpoint.component';
import { LocationHelper, makeLocation$ } from '../utils/location-helper';
import { scaleInjectionToken } from '../vector-renderer/vector-renderer.component';
import {
  buildDisplay,
  buildSubpoints,
  getPointString,
  updateCursorSubpointsAdd,
  updateCursorSubpointsDelete,
} from './abstract-shape-helpers';

@Directive()
export abstract class AbstractShapeComponent
  extends DirectiveSuperclass
  implements OnInit
{
  @Input({ required: true }) shapeStore!: Store<ShapeState2D>;
  @Input({ required: true }) isInOverlay!: boolean;
  @HostBinding('style.display') display!: string;
  @HostBinding('class.selected') selected!: boolean;
  @HostBinding('style.pointer-events') pointerEvents = 'auto';

  isFocused$!: Observable<boolean>;
  location$!: Observable<ShapeLocationState2D>;
  pointsString$!: Observable<string>;
  visibility$!: Observable<VisibilityState>;
  points$!: Observable<Point2D[]>;
  allSubpointInfo$!: Observable<SubpointInfo[]>;
  scale$ = inject(scaleInjectionToken);
  showBorderAndSubpoints$!: Observable<boolean>;

  protected mouseEventService = inject(MouseEventService);
  protected store = inject(AbstractSensorContentStore2D);
  protected isSelected$!: Observable<boolean>;
  protected elementRef = inject(ElementRef<SVGElement>);

  private isHovered$!: Observable<boolean>;
  private isTrainingShape$!: Observable<boolean>;

  constructor(protected injector: Injector) {
    super(injector);
    this.elementRef = injector.get(ElementRef);
    this.scale$ = injector.get(scaleInjectionToken);
  }

  trackByIndex = (index: number) => index;

  ngOnInit() {
    // Note: the order of these matter
    this.location$ = this.buildLocation$();
    this.points$ = this.buildPoints$();
    this.pointsString$ = this.points$.pipe(map(getPointString));
    this.isSelected$ = this.buildIsSelected$();
    this.isFocused$ = this.buildIsFocused$();
    this.visibility$ = this.buildVisibility$();
    this.allSubpointInfo$ = this.buildAllSubpointInfo$();
    this.isHovered$ = this.buildIsHovered$();
    this.showBorderAndSubpoints$ = this.buildSubpointsVisibility$();
    this.isTrainingShape$ = this.buildIsTrainingShape$();

    this.bindToInstance('selected', this.isSelected$);
    this.bindToInstance('display', this.buildDisplay$());
    this.bindToInstance(
      'pointerEvents',
      this.isTrainingShape$.pipe(
        map((isTraining) => (isTraining ? 'none' : 'auto')),
      ),
    );

    if (this.store.state().project.use_annotation_store) {
      this.observeColor();
    } else {
      this.observeColorObjectless();
    }
    this.observeSubpointDeletability();
    this.observeSubpointDragabilityInAddMode();
  }

  handleMouseEvent(
    event: MouseEvent,
    target: 'shape' | 'subpoint' | 'wholeShape',
    subpointIndex?: number,
  ) {
    this.mouseEventService.handle({
      target,
      shapeStore: this.shapeStore,
      subpointIndex,
      event,
    });
  }

  protected buildAllSubpointInfo$(): Observable<SubpointInfo[]> {
    return combineLatest([
      this.store('selection').$,
      this.shapeStore('index').$,
      this.points$,
    ]).pipe(map(buildSubpoints));
  }

  protected observeSubpointDeletability() {
    const minPoints = getShapeHelper(
      this.shapeStore.state().type,
    ).getMinPoints();
    this.subscribeTo(
      combineLatest([
        this.store('editMode').$.pipe(
          map((editMode) => editMode === 'remove'),
          distinctUntilChanged(),
        ),
        this.points$.pipe(
          map((points) => points.length > minPoints),
          distinctUntilChanged(),
        ),
        this.isSelected$,
      ]),
      (values) =>
        updateCursorSubpointsDelete(
          values,
          !!this.store.getFocusedShapeStore(),
          this.elementRef,
        ),
    );
  }

  protected observeSubpointDragabilityInAddMode() {
    this.subscribeTo(
      combineLatest([this.store('editMode').$, this.isSelected$]),
      (values) => updateCursorSubpointsAdd(values, this.elementRef),
    );
  }

  protected buildSubpointsVisibility$() {
    const hoveredOrSelected$ = combineLatest([
      this.isHovered$,
      this.isSelected$,
    ]).pipe(map(([isHovered, isSelected]) => isSelected || isHovered));

    return this.store('layerSettings')('alwaysShowSubpoints').$.pipe(
      switchMap((alwaysShowSubpoints) => {
        const isInProgressShape =
          this.shapeStore.state().index === IN_PROGRESS_INDEX;
        return alwaysShowSubpoints
          ? of(!isInProgressShape)
          : hoveredOrSelected$;
      }),
    );
  }

  private buildLocation$() {
    return makeLocation$(
      new LocationHelper(this.store),
      this.store('frameNumber').$,
      this.shapeStore('key_locations').$,
    ).pipe(cache());
  }

  private buildPoints$() {
    return this.location$.pipe(
      // minor performance optimization: if the shape isn't visible, don't
      // bother moving it around
      filterBehavior(isVisible),
      map((location) => location.points),
      distinctUntilChanged(),
      // orthographic shapes have a z value we want to trim
      map((points) => points.map<Point2D>((point) => [point[0], point[1]])),
      cache(),
    );
  }

  private buildVisibility$() {
    return this.location$.pipe(
      pluck('visibility'),
      distinctUntilChanged(),
      cache(),
    );
  }

  private buildDisplay$() {
    return combineLatest([
      this.visibility$,
      this.store('viewMode').$,
      this.isSelected$,
      this.isTrainingShape$,
    ]).pipe(
      map(([visibility, viewMode, isSelected, isTrainingShape]) =>
        buildDisplay(
          { visibility, viewMode, isSelected, isTrainingShape },
          this.isInOverlay,
        ),
      ),
    );
  }

  private buildIsSelected$() {
    return combineLatest([
      this.store('selection').$,
      this.shapeStore('index').$,
    ]).pipe(
      map(([selection, index]) => !!get(selection, [index])),
      distinctUntilChanged(),
    );
  }

  private buildIsFocused$() {
    // Temporary hack to work around unstable shapeStore in tests.
    const index = this.shapeStore.state().index;
    return this.store.focusedShapeStore$.pipe(
      map((focusedShapeStore) => focusedShapeStore?.state().index === index),
    );
  }

  private buildIsHovered$() {
    // A single shape can trigger multiple mousein and mouseout events
    // based on their subparts (shapefill, subpoints, etc.) We are
    // always interested in the last one emited, since it represents
    // the correct hover status.
    const mouseover$ = this.mouseEventService.getEvent$(
      'wholeShape',
      'mouseover',
      { modifiers: 'any' },
    );

    const mouseout$ = this.mouseEventService.getEvent$(
      'wholeShape',
      'mouseout',
      { modifiers: 'any' },
    );

    return merge(mouseover$, mouseout$).pipe(
      filter(
        (eventInfo) =>
          eventInfo.shapeStore.state().index === this.shapeStore.state().index,
      ),
      map((eventInfo) => eventInfo.event.type === 'mouseover'),
      startWith(false),
    );
  }

  private buildIsTrainingShape$() {
    return this.shapeStore('index').$.pipe(
      map((shapeIndex) => shapeIndex === TRAINING_INDEX),
    );
  }

  private observeColor() {
    const color$ = combineLatest([
      this.store('shapeTransientInfo')(this.shapeStore.state().index).$,
      this.isTrainingShape$,
    ]).pipe(
      map(([shapeTransientInfo, isTrainingShape]) =>
        isTrainingShape
          ? getTrainingShapeColor(this.shapeStore)
          : shapeTransientInfo?.color,
      ),
      // while in progress, transient info is undefined
      filter(isDefined),
    );

    this.subscribeTo(color$, (color) => {
      setCssProperty(this.elementRef, '--h3-shape-color', color);
    });
  }

  /** @deprecated used for old projects without objects */
  private observeColorObjectless() {
    this.subscribeTo(
      this.isTrainingShape$.pipe(
        filter(() => isDefined(this.shapeStore.state())),
        switchMap((isTrainingShape) =>
          isTrainingShape
            ? of(getTrainingShapeColor(this.shapeStore))
            : getShapeColorObjectless$(
                this.shapeStore,
                this.store('frameNumber'),
                this.store.state().output,
                getColorOutput(
                  this.store.state().output.meta.nested_output_color_id,
                  this.store.state().project.outputs,
                ),
              ),
        ),
      ),
      (color) => {
        setCssProperty(this.elementRef, '--h3-shape-color', color);
        if (this.shapeStore.state().transparent) {
          setCssProperty(this.elementRef, '--h3-outline-color', '#000');
        }
      },
    );
  }
}
